import * as THREE from 'three';
import type { Quality } from './renderer';

const MAX_PLAYERS = 4;
const END_OF_TIME_ERA = 9;
const GLOW_COLORS = [0x9cecff, 0xffcf91, 0xd5b8ff, 0xaaffc0];

export interface PlayerGlowProfile {
  lightIntensity: number;
  lightDistance: number;
  auraOpacity: number;
  auraRadius: number;
}

/** Bounded presets keep the accessibility light useful without scaling its cost. */
export function playerGlowProfile(quality: Quality, reducedFlash: boolean): PlayerGlowProfile {
  const profile = quality === 'low'
    ? { lightIntensity: 0.85, lightDistance: 9, auraOpacity: 0.1, auraRadius: 2.5 }
    : quality === 'high'
      ? { lightIntensity: 1.5, lightDistance: 12, auraOpacity: 0.18, auraRadius: 3.5 }
      : { lightIntensity: 1.2, lightDistance: 10.5, auraOpacity: 0.14, auraRadius: 3 };
  return reducedFlash ? { ...profile, auraOpacity: Math.min(profile.auraOpacity, 0.1) } : profile;
}

interface GlowSource {
  group: THREE.Group;
  light: THREE.PointLight;
  aura: THREE.Mesh<THREE.CircleGeometry, THREE.ShaderMaterial>;
}

function auraMaterial(color: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      color: { value: new THREE.Color(color) },
      opacity: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 color;
      uniform float opacity;
      varying vec2 vUv;
      void main() {
        float distanceFromCenter = length(vUv - 0.5) * 2.0;
        float falloff = smoothstep(1.0, 0.0, distanceFromCenter);
        falloff *= falloff;
        gl_FragColor = vec4(color, opacity * falloff);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
}

/**
 * Presentation-only local light for the End of Time. Four fixed, shadowless
 * sources cover the maximum party size without creating objects per frame.
 */
export class PlayerGlow {
  private readonly sources: GlowSource[] = [];

  constructor(scene: THREE.Scene) {
    for (let i = 0; i < MAX_PLAYERS; i++) {
      const group = new THREE.Group();
      const color = GLOW_COLORS[i];
      const light = new THREE.PointLight(color, 0, 0, 2);
      light.position.y = 2.4;
      light.castShadow = false;
      const material = auraMaterial(color);
      const aura = new THREE.Mesh(new THREE.CircleGeometry(1, 32), material);
      aura.rotation.x = -Math.PI / 2;
      aura.position.y = 0.035;
      group.add(light, aura);
      group.visible = false;
      scene.add(group);
      this.sources.push({ group, light, aura });
    }
  }

  update(
    eraIndex: number | null,
    quality: Quality,
    reducedFlash: boolean,
    players: readonly { x: number; z: number; alive: boolean }[],
  ): void {
    const active = eraIndex === END_OF_TIME_ERA;
    const profile = playerGlowProfile(quality, reducedFlash);
    for (let i = 0; i < this.sources.length; i++) {
      const source = this.sources[i];
      const player = players[i];
      source.group.visible = active && !!player?.alive;
      if (!source.group.visible || !player) continue;
      source.group.position.set(player.x, 0, player.z);
      source.light.intensity = profile.lightIntensity;
      source.light.distance = profile.lightDistance;
      source.aura.material.uniforms.opacity.value = profile.auraOpacity;
      source.aura.scale.setScalar(profile.auraRadius);
    }
  }

  hide(): void {
    for (const source of this.sources) source.group.visible = false;
  }
}
